import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { getLearningLevelTranslation, addLearningLevelTranslation, saveTranslationsToFile } from '../../services/learningLevelsTranslationsService';
import { 
  LayersIcon, 
  PlusIcon,
  EditIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  GlobeIcon,
  HashIcon,
  FileTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalendarIcon
} from 'lucide-react';

const LearningLevels = () => {
  const { t } = useTranslation();
  const { isRTL, currentLanguage } = useLanguage();
  
  // State management
  const [learningLevels, setLearningLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(10);
  
  // Add modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [newLearningLevel, setNewLearningLevel] = useState({
    level_number: '',
    description: '',
    translations: {
      ar: '',
      fr: '',
      en: ''
    }
  });
  const [formErrors, setFormErrors] = useState({});

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingLearningLevel, setEditingLearningLevel] = useState({
    id: '',
    name: '',
    level_number: '',
    description: ''
  });
  const [editFormErrors, setEditFormErrors] = useState({});

  // Fetch learning levels
  const fetchLearningLevels = async (page = 1) => {
    setLoading(true);
    try {
      const url = `/organization/learning-levels?page=${page}&limit=${limit}`;
      const response = await api.get(url);
      
      if (response.data.success) {
        setLearningLevels(response.data.data.learningLevels || []);
        setTotal(response.data.data.pagination.total || 0);
        setCurrentPage(response.data.data.pagination.page || 1);
      }
    } catch (error) {
      console.error('Error fetching learning levels:', error);
      toast.error(t('learningLevels.fetchError'), { duration: 3000, position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLearningLevels(currentPage);
  }, []);

  // Translate learning level name
  const translateLearningLevelName = (name, language) => {
    const translation = getLearningLevelTranslation(name);
    if (translation) {
      return translation[language] || name;
    }
    return name;
  };

  // Open add modal
  const openAddModal = () => {
    setNewLearningLevel({
      level_number: '',
      description: '',
      translations: {
        ar: '',
        fr: '',
        en: ''
      }
    });
    setFormErrors({});
    setShowAddModal(true);
  };

  // Close add modal
  const closeAddModal = () => {
    setShowAddModal(false);
    setNewLearningLevel({
      level_number: '',
      description: '',
      translations: {
        ar: '',
        fr: '',
        en: ''
      }
    });
    setFormErrors({});
  };

  // Add learning level
  const addLearningLevel = async (e) => {
    e.preventDefault();
    setFormErrors({});

    // Validation
    const errors = {};
    if (!newLearningLevel.level_number || newLearningLevel.level_number < 1) {
      errors.level_number = t('learningLevels.levelNumberRequired');
    }
    if (!newLearningLevel.translations.ar?.trim()) {
      errors.name_ar = t('learningLevels.nameRequired_ar');
    }
    if (!newLearningLevel.translations.fr?.trim()) {
      errors.name_fr = t('learningLevels.nameRequired_fr');
    }
    if (!newLearningLevel.translations.en?.trim()) {
      errors.name_en = t('learningLevels.nameRequired_en');
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsAdding(true);

    try {
      const requestData = {
        name: newLearningLevel.translations.ar,
        levelNumber: parseInt(newLearningLevel.level_number),
        description: newLearningLevel.description || ''
      };

      const response = await api.post('/organization/learning-levels', requestData);
      
      if (response.data.success) {
        // Add translation using the Arabic name as key
        addLearningLevelTranslation(
          newLearningLevel.translations.ar,
          newLearningLevel.translations.ar,
          newLearningLevel.translations.fr,
          newLearningLevel.translations.en
        );
        
        toast.success(response.data.message || t('learningLevels.addSuccess'), { duration: 3000, position: 'top-center' });
        closeAddModal();
        fetchLearningLevels(currentPage);
      }
    } catch (error) {
      console.error('Error adding learning level:', error);
      const errorMessage = error.response?.data?.message || error.message || t('learningLevels.addError');
      toast.error(errorMessage, { duration: 3000, position: 'top-center' });
    } finally {
      setIsAdding(false);
    }
  };

  // Toggle status
  const toggleStatus = async (learningLevelId, currentStatus) => {
    try {
      const response = await api.patch(`/organization/learning-levels/${learningLevelId}/status`, {
        is_active: !currentStatus
      });
      
      if (response.data.success) {
        toast.success(t('learningLevels.statusUpdated'), { duration: 3000, position: 'top-center' });
        fetchLearningLevels(currentPage);
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error(t('learningLevels.statusUpdateError'), { duration: 3000, position: 'top-center' });
    }
  };

  // Handle edit learning level
  const handleEditLearningLevel = (learningLevel) => {
    setEditingLearningLevel({
      id: learningLevel.id,
      name: learningLevel.name,
      level_number: learningLevel.level_number,
      description: learningLevel.description || ''
    });
    setEditFormErrors({});
    setShowEditModal(true);
  };

  // Close edit modal
  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingLearningLevel({
      id: '',
      name: '',
      level_number: '',
      description: ''
    });
    setEditFormErrors({});
  };

  // Update learning level
  const updateLearningLevel = async (e) => {
    e.preventDefault();
    setEditFormErrors({});

    // Validation
    const errors = {};
    if (!editingLearningLevel.name?.trim()) {
      errors.name = t('learningLevels.nameRequired');
    }
    if (!editingLearningLevel.level_number || editingLearningLevel.level_number < 1) {
      errors.level_number = t('learningLevels.levelNumberRequired');
    }

    if (Object.keys(errors).length > 0) {
      setEditFormErrors(errors);
      return;
    }

    setIsEditing(true);

    try {
      const requestData = {
        name: editingLearningLevel.name,
        levelNumber: parseInt(editingLearningLevel.level_number),
        description: editingLearningLevel.description || ''
      };

      const response = await api.put(`/organization/learning-levels/${editingLearningLevel.id}`, requestData);
      
      if (response.data.success) {
        toast.success(response.data.message || t('learningLevels.updateSuccess'), { duration: 3000, position: 'top-center' });
        closeEditModal();
        fetchLearningLevels(currentPage);
      }
    } catch (error) {
      console.error('Error updating learning level:', error);
      const errorMessage = error.response?.data?.message || error.message || t('learningLevels.updateError');
      toast.error(errorMessage, { duration: 3000, position: 'top-center' });
    } finally {
      setIsEditing(false);
    }
  };

  // Delete learning level
  const deleteLearningLevel = async (learningLevelId, learningLevelName) => {
    if (window.confirm(t('learningLevels.deleteConfirm', { name: learningLevelName }))) {
      try {
        const response = await api.delete(`/organization/learning-levels/${learningLevelId}`);
        
        if (response.data.success) {
          toast.success(response.data.message || t('learningLevels.deleteSuccess'), { duration: 3000, position: 'top-center' });
          fetchLearningLevels(currentPage);
        }
      } catch (error) {
        console.error('Error deleting learning level:', error);
        const errorMessage = error.response?.data?.message || error.message || t('learningLevels.deleteError');
        toast.error(errorMessage, { duration: 3000, position: 'top-center' });
      }
    }
  };

  // Export translations
  const handleExportTranslations = () => {
    try {
      saveTranslationsToFile();
      toast.success(t('learningLevels.exportSuccess'), { duration: 3000, position: 'top-center' });
    } catch (error) {
      console.error('Error exporting translations:', error);
      toast.error(t('learningLevels.exportError'), { duration: 3000, position: 'top-center' });
    }
  };

  // Pagination
  const totalPages = Math.ceil(total / limit);
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchLearningLevels(page);
    }
  };

  // Generate page numbers
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  return (
    <main className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="p-6">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <LayersIcon className={`h-8 w-8 text-primary-600 ${isRTL ? 'ml-3' : 'mr-3'}`} />
                <div className={isRTL ? 'text-right' : 'text-left'}>
                  <h1 className="text-2xl font-bold text-gray-900">{t('learningLevels.title')}</h1>
                  <p className="text-gray-600 mt-1">{t('learningLevels.subtitle')}</p>
                </div>
              </div>
              <div className={`flex ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                <button
                  onClick={openAddModal}
                  className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center"
                >
                  <PlusIcon className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {t('learningLevels.addLevel')}
                </button>
                <button
                  onClick={handleExportTranslations}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center"
                >
                  <GlobeIcon className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {t('learningLevels.exportTranslations')}
                </button>
              </div>
            </div>
          </div>


          {/* Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('learningLevels.levelName')}
                    </th>
                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('learningLevels.levelNumber')}
                    </th>
                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('learningLevels.description')}
                    </th>
                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('common.status')}
                    </th>
                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('common.createdAt')}
                    </th>
                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                        {t('common.loading')}
                      </td>
                    </tr>
                  ) : learningLevels.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                        {t('learningLevels.noLearningLevels')}
                      </td>
                    </tr>
                  ) : (
                    learningLevels.map((level) => (
                      <tr key={level.id} className="hover:bg-gray-50">
                        <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8">
                              <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                                <LayersIcon className="h-4 w-4 text-primary-600" />
                              </div>
                            </div>
                            <div className={`${isRTL ? 'mr-4' : 'ml-4'} ${isRTL ? 'text-right' : 'text-left'}`}>
                              <div className="text-sm font-medium text-gray-900">
                                {translateLearningLevelName(level.name, currentLanguage || 'fr')}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                          <div className="flex items-center">
                            <HashIcon className={`h-4 w-4 text-gray-400 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {level.level_number}
                            </span>
                          </div>
                        </td>
                        <td className={`px-6 py-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                          <div className="flex items-center">
                            <FileTextIcon className={`h-4 w-4 text-gray-400 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <div className="text-sm text-gray-900 max-w-xs truncate">
                              {level.description || '-'}
                            </div>
                          </div>
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                          <button
                            onClick={() => toggleStatus(level.id, level.is_active)}
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors cursor-pointer ${
                              level.is_active
                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                : 'bg-red-100 text-red-800 hover:bg-red-200'
                            }`}
                          >
                            {level.is_active ? (
                              <>
                                <CheckCircleIcon className={`h-3 w-3 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                                {t('common.active')}
                              </>
                            ) : (
                              <>
                                <XCircleIcon className={`h-3 w-3 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                                {t('common.inactive')}
                              </>
                            )}
                          </button>
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
                          <div className="flex items-center">
                            <CalendarIcon className={`h-4 w-4 text-gray-400 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            {new Date(level.created_at).toLocaleDateString(
                              currentLanguage === 'en' ? 'en-US' : 'fr-FR',
                              {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              }
                            )}
                          </div>
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                          <div className={`flex items-center ${isRTL ? 'justify-end space-x-reverse space-x-2' : 'justify-start space-x-2'}`}>
                            <button
                              onClick={() => handleEditLearningLevel(level)}
                              className="text-primary-600 hover:text-primary-900"
                              title={t('learningLevels.edit')}
                            >
                              <EditIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => deleteLearningLevel(level.id, translateLearningLevelName(level.name, currentLanguage || 'fr'))}
                              className="text-red-600 hover:text-red-900"
                              title={t('learningLevels.delete')}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!loading && learningLevels.length > 0 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${
                      currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {t('common.previous')}
                  </button>
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${
                      currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {t('common.next')}
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div className={isRTL ? 'text-right' : 'text-left'}>
                    <p className="text-sm text-gray-700">
                      {t('common.showing')} <span className="font-medium">{(currentPage - 1) * limit + 1}</span> {t('common.to')}{' '}
                      <span className="font-medium">{Math.min(currentPage * limit, total)}</span> {t('common.of')}{' '}
                      <span className="font-medium">{total}</span> {t('common.results')}
                    </p>
                  </div>
                  <div>
                    <nav className={`relative z-0 inline-flex rounded-md shadow-sm ${isRTL ? 'space-x-reverse -space-x-px' : '-space-x-px'}`}>
                      <button
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 ${
                          currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        <ChevronLeftIcon className="h-5 w-5" />
                      </button>
                      {getPageNumbers().map((pageNum, index) => (
                        pageNum === '...' ? (
                          <span key={`ellipsis-${index}`} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                            ...
                          </span>
                        ) : (
                          <button
                            key={pageNum}
                            onClick={() => goToPage(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === pageNum
                                ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        )
                      ))}
                      <button
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 ${
                          currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        <ChevronRightIcon className="h-5 w-5" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" onClick={closeAddModal}>
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">{t('learningLevels.addLearningLevel')}</h3>
              <button onClick={closeAddModal} className="text-gray-400 hover:text-gray-500">
                <span className="text-2xl">&times;</span>
              </button>
            </div>
            
            <form onSubmit={addLearningLevel}>
              <div className="space-y-4">
                {/* Level Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('learningLevels.levelNumber')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={newLearningLevel.level_number}
                    onChange={(e) => setNewLearningLevel({ ...newLearningLevel, level_number: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      formErrors.level_number ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {formErrors.level_number && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.level_number}</p>
                  )}
                </div>

                {/* Arabic Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('learningLevels.levelName')} ({t('common.arabic')}) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newLearningLevel.translations.ar}
                    onChange={(e) => setNewLearningLevel({
                      ...newLearningLevel,
                      translations: { ...newLearningLevel.translations, ar: e.target.value }
                    })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      formErrors.name_ar ? 'border-red-500' : 'border-gray-300'
                    }`}
                    dir="rtl"
                  />
                  {formErrors.name_ar && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.name_ar}</p>
                  )}
                </div>

                {/* French Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('learningLevels.levelName')} ({t('common.french')}) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newLearningLevel.translations.fr}
                    onChange={(e) => setNewLearningLevel({
                      ...newLearningLevel,
                      translations: { ...newLearningLevel.translations, fr: e.target.value }
                    })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      formErrors.name_fr ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {formErrors.name_fr && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.name_fr}</p>
                  )}
                </div>

                {/* English Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('learningLevels.levelName')} ({t('common.english')}) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newLearningLevel.translations.en}
                    onChange={(e) => setNewLearningLevel({
                      ...newLearningLevel,
                      translations: { ...newLearningLevel.translations, en: e.target.value }
                    })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      formErrors.name_en ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {formErrors.name_en && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.name_en}</p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('learningLevels.description')}
                  </label>
                  <textarea
                    value={newLearningLevel.description}
                    onChange={(e) => setNewLearningLevel({ ...newLearningLevel, description: e.target.value })}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className={`mt-6 flex ${isRTL ? 'justify-start space-x-reverse space-x-3' : 'justify-end space-x-3'}`}>
                <button
                  type="button"
                  onClick={closeAddModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isAdding}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  {isAdding ? t('learningLevels.adding') : t('learningLevels.add')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" onClick={closeEditModal}>
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">{t('learningLevels.editLearningLevel')}</h3>
              <button onClick={closeEditModal} className="text-gray-400 hover:text-gray-500">
                <span className="text-2xl">&times;</span>
              </button>
            </div>
            
            <form onSubmit={updateLearningLevel}>
              <div className="space-y-4">
                {/* Level Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('learningLevels.levelNumber')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={editingLearningLevel.level_number}
                    onChange={(e) => setEditingLearningLevel({ ...editingLearningLevel, level_number: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      editFormErrors.level_number ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {editFormErrors.level_number && (
                    <p className="mt-1 text-sm text-red-600">{editFormErrors.level_number}</p>
                  )}
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('learningLevels.levelName')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editingLearningLevel.name}
                    onChange={(e) => setEditingLearningLevel({ ...editingLearningLevel, name: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      editFormErrors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {editFormErrors.name && (
                    <p className="mt-1 text-sm text-red-600">{editFormErrors.name}</p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('learningLevels.description')}
                  </label>
                  <textarea
                    value={editingLearningLevel.description}
                    onChange={(e) => setEditingLearningLevel({ ...editingLearningLevel, description: e.target.value })}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className={`mt-6 flex ${isRTL ? 'justify-start space-x-reverse space-x-3' : 'justify-end space-x-3'}`}>
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isEditing}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  {isEditing ? t('learningLevels.updating') : t('learningLevels.update')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
};

export default LearningLevels;

